/**
 * Business-day key ("YYYY-MM-DD") for "today" in AST (UTC-4, no DST).
 *
 * The −4h shift moves the instant so that the UTC getters read AST wall-clock
 * time. Always read the shifted value with getUTC* (or toISOString) — never
 * with device-local getters like toDateString()/getDate(), which re-apply the
 * device offset and double-subtract 4h on DR phones (EV-0230 symptom family:
 * between 00:00 and ~04:00 AST "today" reads as yesterday).
 */
export function todayIsoAst(): string {
  const ast = new Date(Date.now() - 4 * 3600 * 1000);
  const y = ast.getUTCFullYear();
  const m = String(ast.getUTCMonth() + 1).padStart(2, '0');
  const d = String(ast.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Call-time instant (epoch ms) for an event card, in AST. `startTime` arrives
 * from getMyEvents as "H:MM AM/PM" (formatTimeSpanish) or "" — a missing or
 * unparseable time falls back to noon, the same conservative rule the backend
 * applies. Mirrors sophi-bot/src/swap.js:computeEventStartMs — the API is the
 * authority (it refuses a swap on a started event whatever the UI shows); any
 * change to the rule must be ported.
 */
export function eventStartMsAst(dateIso: string, startTime: string): number | null {
  const m = String(dateIso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  let hh = 12;
  let mm = 0;
  // Meridiem is all-or-nothing: "5:00 m" must NOT parse (the API treats it
  // as unparseable → noon), so a stray trailing letter can't be swallowed.
  const t = String(startTime || '').trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([AaPp])\.?[Mm]\.?)?$/);
  if (t) {
    hh = Number(t[1]) % 24;
    mm = Number(t[2]);
    if (t[3]) {
      hh = hh % 12;
      if (t[3].toLowerCase() === 'p') hh += 12;
    }
  }
  // AST = UTC−4, no DST: AST wall-clock → UTC instant by adding 4h.
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), hh, mm) + 4 * 3600 * 1000;
}

/** True once the event's call time is behind us (AST). Unresolvable date → true (fail closed, like the API). */
export function eventHasStartedAst(dateIso: string, startTime: string, nowMs: number = Date.now()): boolean {
  const start = eventStartMsAst(dateIso, startTime);
  if (start === null) return true;
  return nowMs >= start;
}

/**
 * Preferred check: the API's `startMs` (computed from the raw sheet cells with
 * the same parser the swap gate uses) is authoritative. The display-string
 * parse above is only the fallback for payloads cached before the field
 * existed.
 */
export function eventHasStarted(
  ev: { date: string; startTime: string; startMs?: number | null },
  nowMs: number = Date.now(),
): boolean {
  if (ev.startMs === null) return true; // server could not resolve the event → fail closed, like the API's 404
  if (typeof ev.startMs === 'number' && Number.isFinite(ev.startMs)) return nowMs >= ev.startMs;
  return eventHasStartedAst(ev.date, ev.startTime, nowMs);
}

/**
 * Cancel rule (mirrors the API's `eventDatePassed`): the event DATE is behind
 * us in AST. Cancels stay available on the event day itself (a late cancel
 * still runs penalty + Ops alert + replacement search), unlike swaps. A
 * server-unresolvable event (`startMs === null`) fails closed.
 */
/**
 * Mirrors the API's late-cancel anchor (miniapp-api routes/events.js
 * computeHoursUntilEvent: NOON AST of the event date, penalty when < 24h) so
 * the confirm dialog warns exactly when the server will charge. Uses the
 * authoritative date behind `startMs` when present; unknown → warn.
 */
export function isLateCancel(ev: { date: string; eventDate?: string }, nowMs: number = Date.now()): boolean {
  // The authoritative date field, never a day derived from a time instant.
  const dayIso = ev.eventDate || ev.date;
  const m = String(dayIso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return true;
  const noonAst = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 16, 0);
  return (noonAst - nowMs) / 3600000 < 24;
}

export function eventDatePassed(
  ev: { date: string; startMs?: number | null; eventDate?: string },
  todayIso: string = todayIsoAst(),
): boolean {
  if (ev.startMs === null) return true;
  const dayIso = ev.eventDate || ev.date;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayIso || '')) return true;
  return dayIso < todayIso;
}
